import { ArrowDown, ArrowUp, X } from "lucide-react";
import DateField from "@/components/DateField";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

function StopRow({ s, i, count, checked, onCheck, onPatch, onMove, onRemove }) {
  return (
    <tr className="border-t hover:bg-secondary/40 transition-colors" data-testid={`route-stop-${i}`}>
      <td className="px-3 py-2"><Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`route-stop-check-${i}`} /></td>
      <td className="px-2 py-2">
        <Input key={i} type="number" min={1} max={count} defaultValue={i + 1}
          onBlur={(e) => { const n = parseInt(e.target.value, 10); if (Number.isNaN(n)) e.target.value = i + 1; else if (n - 1 !== i) onMove(n - 1); }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="w-16 h-8 font-mono" aria-label="Stop order" data-testid={`route-stop-order-${i}`} />
      </td>
      <td className="px-3 py-2 font-mono text-xs" data-testid={`route-stop-job-${i}`}>{s.job || "—"}</td>
      <td className="px-3 py-2"><p className="font-medium">{s.name || "—"}</p><p className="text-xs text-muted-foreground">{s.city}</p></td>
      <td className="px-3 py-2 min-w-[190px]"><DateField id={`route-stop-date-${i}`} value={s.date} onChange={(v) => onPatch({ date: v })} /></td>
      <td className="px-3 py-2">
        <Input value={s.number || ""} onChange={(e) => onPatch({ number: e.target.value })} placeholder="Invoice #" maxLength={30}
          className="w-36 h-8 font-mono" data-testid={`route-stop-number-${i}`} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => onMove(i - 1)} aria-label="Move up" data-testid={`route-stop-up-${i}`}><ArrowUp size={14} /></Button>
        <Button size="icon" variant="ghost" disabled={i === count - 1} onClick={() => onMove(i + 1)} aria-label="Move down" data-testid={`route-stop-down-${i}`}><ArrowDown size={14} /></Button>
        <Button size="icon" variant="ghost" onClick={onRemove} aria-label="Remove stop" data-testid={`route-stop-remove-${i}`}><X size={14} /></Button>
      </td>
    </tr>
  );
}

export default function RouteStopsTable({ stops, setStops, selected, setSelected }) {
  const patch = (i, p) => setStops((prev) => prev.map((s, k) => (k === i ? { ...s, ...p } : s)));
  const move = (i, to) => setStops((prev) => {
    if (Number.isNaN(to)) return prev;
    const t = Math.max(0, Math.min(prev.length - 1, to));
    const next = [...prev];
    const [s] = next.splice(i, 1);
    next.splice(t, 0, s);
    return next;
  });
  const remove = (i) => setStops((prev) => prev.filter((_, k) => k !== i));
  const toggle = (id, on) => setSelected((prev) => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });
  const all = stops.length > 0 && stops.every((s) => selected.has(s.record_id));

  return (
    <div className="border rounded-lg bg-card overflow-x-auto" data-testid="route-stops-table">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-left">
          <tr>
            <th className="px-3 py-2.5"><Checkbox checked={all} onCheckedChange={(on) => setSelected(on ? new Set(stops.map((s) => s.record_id)) : new Set())} data-testid="route-stop-check-all" /></th>
            {["Order", "Job #", "Customer", "Invoice date", "Invoice #", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {stops.map((s, i) => (
            <StopRow key={s.record_id} s={s} i={i} count={stops.length} checked={selected.has(s.record_id)}
              onCheck={(on) => toggle(s.record_id, on)} onPatch={(p) => patch(i, p)} onMove={(to) => move(i, to)} onRemove={() => remove(i)} />
          ))}
        </tbody>
      </table>
      {stops.length === 0 && <p className="p-10 text-center text-muted-foreground" data-testid="route-stops-empty">No stops yet. Pull a route or add customers above.</p>}
    </div>
  );
}
